import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import SEO from '../components/SEO';
import { useAuthModal } from '../context/AuthModalContext';
import Navbar from '../components/Navbar';

const API = process.env.REACT_APP_BACKEND_URL;

function formatTime(seconds) {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function timeAgo(dateStr) {
  const diff  = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (days > 0)  return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${mins}m ago`;
}

function StatCard({ label, value, sub, accent, c }) {
  return (
    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px 24px', transition: 'background 0.25s, border-color 0.25s', boxShadow: c.shadow }}>
      <p style={{ fontSize: '11px', fontWeight: 600, color: c.secondary, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</p>
      <p style={{ fontSize: '32px', fontWeight: 800, color: accent || c.text, margin: '0 0 4px', letterSpacing: '-1px' }}>{value}</p>
      {sub && <p style={{ fontSize: '12px', color: c.secondary, margin: 0 }}>{sub}</p>}
    </div>
  );
}

// Skeleton card
function SkeletonCard({ c }) {
  return <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px 24px', height: 96 }} />;
}

export default function Profile() {
  const { openAuthModal } = useAuthModal();
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const { theme } = useTheme();
  const c         = TOKENS[theme];

  const difficultyColor = { Easy: c.success, Medium: c.warning, Hard: c.error };
  const difficultyBg    = {
    Easy:   theme === 'light' ? '#d1fae5' : '#051a10',
    Medium: theme === 'light' ? '#fef3c7' : '#1a1000',
    Hard:   theme === 'light' ? '#fee2e2' : '#1a0808',
  };
  const successBg     = theme === 'light' ? '#d1fae5' : '#051a10';
  const successBorder = theme === 'light' ? '#6ee7b7' : '#065f46';

  const [progress, setProgress] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const fetchData = async () => {
      try {
        const progressRes  = await fetch(`${API}/progress/${user.id}`);
        const progressData = await progressRes.json();
        let badgesData = [];
        try {
          const badgesRes = await fetch(`${API}/user-badges/${user.id}`);
          if (badgesRes.ok) badgesData = await badgesRes.json();
        } catch (e) { badgesData = []; }
        setProgress({ ...progressData, badges: badgesData || [] });
      } catch (e) {
        console.error('Fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  if (!user) return (
    <div style={{ background: c.bg, minHeight: '100vh', transition: 'background 0.25s' }}>
      <Navbar />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 58px)' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: c.secondary, marginBottom: '16px' }}>Sign in to see your progress</p>
          <button onClick={() => openAuthModal()} style={{ background: c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '10px 24px', borderRadius: '7px', cursor: 'pointer', fontWeight: 700 }}>Sign in with Google</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif", transition: 'background 0.25s, color 0.25s' }}>
      <SEO title="My Progress — TrailCode" description="View your TrailCode profile, challenge history, and coding progress." url="https://trailcode.org/profile" noIndex={true} />

      <Navbar />

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '40px clamp(24px, 4vw, 60px) 80px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '36px' }}>
          {user.user_metadata?.avatar_url && <img src={user.user_metadata.avatar_url} alt="avatar" style={{ width: '56px', height: '56px', borderRadius: '50%', border: `2px solid ${c.border}` }} />}
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.3px', color: c.text }}>{user.user_metadata?.full_name}</h1>
            <p style={{ fontSize: '13px', color: c.secondary, margin: 0 }}>{user.email}</p>
          </div>
        </div>

        {loading ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
              {[...Array(4)].map((_, i) => <SkeletonCard key={i} c={c} />)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {[...Array(2)].map((_, i) => <div key={i} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, height: 260 }} />)}
            </div>
          </>
        ) : progress && (
          <>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '28px' }}>
              <StatCard label="Challenges Solved"  value={progress.totalSolved}           sub={`of ${progress.totalSolved + (progress.totalAttempts - progress.totalSolved)} attempted`} accent={c.accent} c={c} />
              <StatCard label="Total Points"        value={progress.points}                sub="Easy 10 · Medium 20 · Hard 30" c={c} />
              <StatCard label="Time Spent Coding"   value={formatTime(progress.totalTime)} sub="across all challenges" c={c} />
              <StatCard label="Day Streak"           value={`${progress.streak}🔥`}        sub="consecutive days with a solve" accent={c.warning} c={c} />
            </div>

            {/* Main grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>

              {/* Progress by topic */}
              <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '24px', boxShadow: c.shadow }}>
                <p style={{ fontSize: '11px', fontWeight: 600, color: c.secondary, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Progress by Topic</p>
                {progress.byTopic?.length > 0 ? progress.byTopic.map(t => (
                  <div key={t.topic} style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13px', color: c.text }}>{t.topic}</span>
                      <span style={{ fontSize: '12px', color: c.secondary }}>{t.solved}</span>
                    </div>
                    <div style={{ height: '3px', background: c.border, borderRadius: '2px' }}>
                      <div style={{ width: `${t.pct}%`, height: '3px', background: c.accent, borderRadius: '2px', transition: 'width 0.4s' }} />
                    </div>
                  </div>
                )) : <p style={{ color: c.secondary, fontSize: 13 }}>No challenges solved yet — start with Apex Fundamentals!</p>}
              </div>

              {/* Difficulty breakdown */}
              <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '24px', boxShadow: c.shadow }}>
                <p style={{ fontSize: '11px', fontWeight: 600, color: c.secondary, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Difficulty Breakdown</p>
                {['Easy','Medium','Hard'].map(diff => (
                  <div key={diff} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: difficultyColor[diff] }}>{diff}</span>
                      <span style={{ fontSize: '12px', color: c.secondary }}>{progress.byDifficulty?.[diff]?.solved || 0} / {progress.byDifficulty?.[diff]?.total || 0}</span>
                    </div>
                    <div style={{ height: '4px', background: c.border, borderRadius: '2px' }}>
                      <div style={{ width: `${progress.byDifficulty?.[diff]?.pct || 0}%`, height: '4px', background: difficultyColor[diff], borderRadius: '2px', transition: 'width 0.4s' }} />
                    </div>
                  </div>
                ))}
                <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '14px 16px', marginTop: '8px' }}>
                  <p style={{ fontSize: '12px', color: c.secondary, margin: '0 0 8px', fontWeight: 600 }}>Points breakdown</p>
                  {[['Easy','10'], ['Medium','20'], ['Hard','30']].map(([diff, pts]) => (
                    <div key={diff} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                      <span style={{ color: c.secondary }}>{progress.byDifficulty?.[diff]?.solved || 0} × {pts} pts</span>
                      <span style={{ color: difficultyColor[diff], fontWeight: 600 }}>{(progress.byDifficulty?.[diff]?.solved || 0) * parseInt(pts)}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 700, borderTop: `1px solid ${c.border}`, paddingTop: '8px', marginTop: '4px' }}>
                    <span style={{ color: c.text }}>Total</span>
                    <span style={{ color: c.accent }}>{progress.points}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent activity */}
            {progress.recentActivity?.length > 0 && (
              <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '24px', boxShadow: c.shadow }}>
                <p style={{ fontSize: '11px', fontWeight: 600, color: c.secondary, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Recent Activity</p>
                {progress.recentActivity.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: i < progress.recentActivity.length - 1 ? `1px solid ${c.border}` : 'none' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: successBg, border: `1px solid ${successBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: c.success, fontSize: 14 }}>✓</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 2px', color: c.text }}>{item.title}</p>
                      <p style={{ fontSize: '11px', color: c.secondary, margin: 0 }}>{item.topic}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: difficultyColor[item.difficulty], background: difficultyBg[item.difficulty], padding: '1px 8px', borderRadius: 10 }}>{item.difficulty}</span>
                      <p style={{ fontSize: '11px', color: c.muted, margin: '4px 0 0' }}>⏱ {formatTime(item.timeTaken)} · {timeAgo(item.solvedAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Keep going CTA */}
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <button onClick={() => navigate('/challenges/apex')} style={{ background: c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '13px 32px', borderRadius: 9, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
                Keep going →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}